import { Component } from "@theme/component";
import {
  triggerTrackEvent,
  hashToSha256,
  hashToMd5,
} from "@theme/tealium-utils";

// Module-level guard: Shopify sets form.posted_successfully? at session level, so
// all custom-form-component instances on the same page find their success element.
// This flag ensures the tracking event fires exactly once per page load.
let pageNewsletterTrackFired = false;
const NEWSLETTER_SHA256_STORAGE_KEY = "newsletter_email_sha256";
const NEWSLETTER_MD5_STORAGE_KEY = "newsletter_email_md5";

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} successElement - The success element.
 * @property {HTMLFormElement} formElement - The form element.
 *
 * @extends {Component<Refs>}
 */

class CustomFormComponent extends Component {
  /** @type {((event: SubmitEvent) => void) | null} */
  #onNewsletterSubmit = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.refs.formElement.id.startsWith("newsletter_form")) {
      this.#onNewsletterSubmit = this.handleNewsletterSubmit.bind(this);
      this.refs.formElement.addEventListener(
        "submit",
        this.#onNewsletterSubmit,
      );
      this.checkPostedSuccess();
    }
  }

  disconnectedCallback() {
    if (this.#onNewsletterSubmit) {
      this.refs.formElement.removeEventListener(
        "submit",
        this.#onNewsletterSubmit,
      );
      this.#onNewsletterSubmit = null;
    }
    super.disconnectedCallback();
  }

  //customer_posted: newsletter
  //contact_posted: contact form
  get paramName() {
    return this.dataset.paramName || "customer_posted";
  }

  async checkPostedSuccess() {
    const form = this.refs.formElement;
    /** @type {HTMLElement | null} */
    const successElement = this.querySelector("#" + form.id + "_success");
    if (successElement) {
      if (!pageNewsletterTrackFired) {
        pageNewsletterTrackFired = true;
        const trackedSha256 = this.#getSessionStorageItem(
          NEWSLETTER_SHA256_STORAGE_KEY,
        );
        const trackedMd5 = this.#getSessionStorageItem(
          NEWSLETTER_MD5_STORAGE_KEY,
        );

        const data = {
          Events_UserEmailSub: "1",
          User_Email_SHA256: trackedSha256 || "",
          User_Email_MD5: trackedMd5 || "",
        };
        triggerTrackEvent(data);
        this.#clearSessionStorageItem(NEWSLETTER_SHA256_STORAGE_KEY);
        this.#clearSessionStorageItem(NEWSLETTER_MD5_STORAGE_KEY);
      }
      this.hideSuccessElement(false);
    }
  }

  async handleNewsletterSubmit() {
    const form = this.refs.formElement;
    if (!(form instanceof HTMLFormElement)) return;

    const emailInput = form.querySelector('input[name="contact[email]"]');

    if (!(emailInput instanceof HTMLInputElement)) return;

    const email = emailInput.value.trim();

    if (!email) return;

    try {
      const sha256 = await hashToSha256(email);
      window.sessionStorage.setItem(NEWSLETTER_SHA256_STORAGE_KEY, sha256);

      const md5 = hashToMd5(email);
      window.sessionStorage.setItem(NEWSLETTER_MD5_STORAGE_KEY, md5);
    } catch (error) {
      console.warn("[custom-form] unable to hash/store sha256", error);
    }
  }

  #getSessionStorageItem(/** @type {string} */ key) {
    try {
      const value = window.sessionStorage.getItem(key);
      if (!value) {
        console.log(
          `[custom-form] no value found in sessionStorage for key: ${key}`,
        );
        return null;
      }

      return value;
    } catch (error) {
      console.warn(
        `[custom-form] unable to read value from sessionStorage for key: ${key}`,
        error,
      );
      return null;
    }
  }

  #clearSessionStorageItem(/** @type {string} */ key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (error) {
      console.warn(
        `[custom-form] unable to clear value from sessionStorage for key: ${key}`,
        error,
      );
    }
  }

  hideSuccessElement(reload = true) {
    //delete parameter from url paramname
    const params = new URLSearchParams(window.location.search);
    if (params.has(this.paramName)) {
      params.delete(this.paramName);

      const newSearch = params.toString();
      const cleanUrl =
        window.location.origin +
        window.location.pathname +
        (newSearch ? "?" + newSearch : "") +
        window.location.hash;
      history.replaceState(null, "", cleanUrl);
    }
    if (reload) {
      window.location.reload();
    }
    if (window.location.hash) {
      const target = this.querySelector(window.location.hash);
      if (target) target.scrollIntoView({ behavior: "smooth" });
    }
  }
}

if (!customElements.get("custom-form-component")) {
  customElements.define("custom-form-component", CustomFormComponent);
}
